import { apiSlice } from "fond/api/apiSlice";
import {
  ImageOverlayItem,
  ImageOverlayPresignedUpload,
  ListCustomOverlaysResponse,
  ListImageOverlaysResponse,
  UpdateImageOverlayRequest,
} from "fond/types/overlays";

/**
 * Custom Overlays & Image Overlays API Slice
 */
export const overlaysSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Custom Overlays (collaboration-sourced)
     */
    getOverlays: build.query<ListCustomOverlaysResponse, { versionId: string } | { multiProjectId: string }>({
      query: (params) => ({
        url: `/v2/custom-overlays`,
        method: "GET",
        params: "versionId" in params ? { version_id: params.versionId } : { multi_project_id: params.multiProjectId },
      }),
      providesTags: (_result, _error, params) => {
        const id = "versionId" in params ? params.versionId : params.multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),
    createOverlay: build.mutation<void, { name: string; sourceVersionId: string; targetType: "VERSION" | "MULTI_PROJECT"; targetId: string }>({
      query: ({ name, sourceVersionId, targetType, targetId }) => ({
        url: `/v2/custom-overlays`,
        method: "POST",
        body: {
          name,
          source_version: {
            id: sourceVersionId,
          },
          target: {
            type: targetType === "VERSION" ? "version" : "multi_project",
            id: targetId,
          },
        },
      }),
      invalidatesTags: (_result, _error, { targetId }) => {
        return [{ type: "Overlay", id: targetId }];
      },
    }),
    updateOverlay: build.mutation<void, { overlayId: string; name: string; versionId?: string; multiProjectId?: string }>({
      query: ({ overlayId, name }) => ({
        url: `/v2/custom-overlays/${overlayId}`,
        method: "PATCH",
        body: { Name: name },
      }),
      invalidatesTags: (_result, _error, { versionId, multiProjectId }) => {
        const id = versionId || multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),
    deleteOverlay: build.mutation<void, { overlayId: string } & ({ versionId: string } | { multiProjectId: string })>({
      query: ({ overlayId }) => ({
        url: `/v2/custom-overlays/${overlayId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, params) => {
        const id = "versionId" in params ? params.versionId : params.multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),

    /**
     * Image Overlays
     */
    getImageOverlays: build.query<ListImageOverlaysResponse, { versionId: string } | { multiProjectId: string }>({
      query: (params) => ({
        url: `/v2/image-overlays`,
        method: "GET",
        params: "versionId" in params ? { version_id: params.versionId } : { multi_project_id: params.multiProjectId },
      }),
      transformResponse: (response: ListImageOverlaysResponse) => ({
        ...response,
        ImageOverlays: response.ImageOverlays.filter((image) => image.Uploaded),
      }),
      providesTags: (_result, _error, params) => {
        const id = "versionId" in params ? params.versionId : params.multiProjectId;
        return [{ type: "ImageOverlay", id }];
      },
    }),
    createImageOverlay: build.mutation<
      ImageOverlayItem,
      {
        name: string;
        imageCorners: ImageOverlayItem["ImageCorners"];
        opacity: number;
        mimeType: string;
        targetType: "version" | "multi_project";
        targetId: string;
      }
    >({
      query: ({ name, imageCorners, opacity, mimeType, targetType, targetId }) => ({
        url: `/v2/image-overlays`,
        method: "POST",
        body: {
          Name: name,
          ImageCorners: imageCorners,
          Opacity: opacity,
          MimeType: mimeType,
          Target: {
            Type: targetType,
            ID: targetId,
          },
        },
      }),
    }),
    getImageOverlayPresignedUpload: build.query<ImageOverlayPresignedUpload, { imageOverlayId: string }>({
      query: ({ imageOverlayId }) => ({
        url: `/v2/image-overlays/${imageOverlayId}/presigned-upload`,
        method: "GET",
      }),
    }),
    updateImageOverlay: build.mutation<ImageOverlayItem, { imageOverlayId: string; targetId: string } & UpdateImageOverlayRequest>({
      query: ({ imageOverlayId, targetId: _, ...body }) => ({
        url: `/v2/image-overlays/${imageOverlayId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { targetId }) => {
        return [{ type: "ImageOverlay", id: targetId }];
      },
    }),
    deleteImageOverlay: build.mutation<void, { imageOverlayId: string; targetId: string }>({
      query: ({ imageOverlayId }) => ({
        url: `/v2/image-overlays/${imageOverlayId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { targetId }) => {
        return [{ type: "ImageOverlay", id: targetId }];
      },
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useGetOverlaysQuery,
  useCreateOverlayMutation,
  useUpdateOverlayMutation,
  useDeleteOverlayMutation,
  useGetImageOverlaysQuery,
  useCreateImageOverlayMutation,
  useGetImageOverlayPresignedUploadQuery,
  useLazyGetImageOverlayPresignedUploadQuery,
  useUpdateImageOverlayMutation,
  useDeleteImageOverlayMutation,
} = overlaysSlice;
